import {
  ArrowRight,
  CheckCircle2,
  Copy,
  LoaderCircle,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  TriangleAlert
} from "lucide-react";
import { useMemo, useState } from "react";
import type {
  AiInsightResponse,
  FinancingResult,
  SimulationInput
} from "../../shared/contracts";
import { deriveOptionFacts } from "../../shared/finance-facts";
import { getAiInsight } from "../lib/api";
import { currencyDetailed, systemLabel } from "../lib/format";

interface GeminiAdvisorProps {
  input: SimulationInput;
  selected: FinancingResult;
  results: FinancingResult[];
}

type AdvisorState =
  | { status: "idle" }
  | { status: "loading"; fingerprint: string }
  | { status: "ready"; fingerprint: string; response: AiInsightResponse }
  | { status: "error"; fingerprint: string; message: string };

async function writeClipboard(contents: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(contents);
    return;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = contents;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    textarea.remove();
    if (!copied) throw new Error("Clipboard unavailable.");
  }
}

export function GeminiAdvisor({ input, selected, results }: GeminiAdvisorProps) {
  const [state, setState] = useState<AdvisorState>({ status: "idle" });
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");
  const facts = useMemo(() => deriveOptionFacts(selected), [selected]);
  const bestEligible = useMemo(
    () =>
      results
        .filter((result) => result.eligibility.eligible)
        .sort((left, right) => left.totalPaid - right.totalPaid)[0],
    [results]
  );
  const scenarioFingerprint = useMemo(
    () =>
      [
        input.propertyValue,
        input.downPayment,
        input.termMonths,
        selected.key,
        selected.firstPayment.toFixed(2),
        selected.totalPaid.toFixed(2)
      ].join(":"),
    [
      input.downPayment,
      input.propertyValue,
      input.termMonths,
      selected.firstPayment,
      selected.key,
      selected.totalPaid
    ]
  );

  const visibleState =
    state.status !== "idle" && state.fingerprint !== scenarioFingerprint
      ? ({ status: "idle" } as const)
      : state;

  const comparisonLabel = (() => {
    if (!bestEligible) return "Nenhuma referência elegível neste cenário";
    if (bestEligible.key === selected.key) return "Menor total entre as opções elegíveis";
    const delta = selected.totalPaid - bestEligible.totalPaid;
    if (!selected.eligibility.eligible && delta < 0) {
      return `${currencyDetailed(Math.abs(delta))} menor, mas fora dos critérios`;
    }
    return `${currencyDetailed(delta)} vs. ${bestEligible.bankShortName} ${systemLabel(bestEligible.system)}`;
  })();

  async function copyDeterministicSummary() {
    const cetLine =
      facts.cetSpreadPp === null
        ? "CET estimado: indisponível"
        : `CET estimado: ${selected.estimatedCetAnnual?.toFixed(2).replace(".", ",")}% a.a. (${facts.cetSpreadPp >= 0 ? "+" : ""}${facts.cetSpreadPp.toFixed(2).replace(".", ",")} p.p. sobre a taxa efetiva)`;
    const summary = [
      `Alicerce — ${selected.bankName} · ${systemLabel(selected.system)}`,
      `Valor financiado: ${currencyDetailed(selected.financedAmount)}`,
      `Primeira parcela: ${currencyDetailed(selected.firstPayment)}`,
      `Última parcela: ${currencyDetailed(selected.lastPayment)}`,
      `Total estimado: ${currencyDetailed(selected.totalPaid)}`,
      `Juros: ${facts.interestSharePct.toFixed(2).replace(".", ",")}% do total`,
      `Seguros e tarifas: ${(facts.insuranceSharePct + facts.adminFeesSharePct + facts.upfrontFeeSharePct).toFixed(2).replace(".", ",")}% do total`,
      cetLine,
      comparisonLabel,
      "Premissas ilustrativas; confirme CET, seguros e tarifas na proposta oficial."
    ].join("\n");
    try {
      await writeClipboard(summary);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
    window.setTimeout(() => setCopyStatus("idle"), 2_400);
  }

  async function generateInsight() {
    const requestedFingerprint = scenarioFingerprint;
    setState({ status: "loading", fingerprint: requestedFingerprint });
    try {
      const response = await getAiInsight(input, selected, results);
      setState({ status: "ready", fingerprint: requestedFingerprint, response });
    } catch (error) {
      setState({
        status: "error",
        fingerprint: requestedFingerprint,
        message:
          error instanceof Error
            ? error.message
            : "A leitura inteligente ficou indisponível. Tente novamente."
      });
    }
  }

  return (
    <section className="ai-advisor" aria-labelledby="ai-advisor-title">
      <div className="ai-advisor__rail" aria-hidden="true">
        <Sparkles />
        <span>GEMINI</span>
        <i />
      </div>

      <div className="ai-advisor__body" aria-live="polite">
        <header className="ai-advisor__header">
          <div>
            <span className="section-kicker">IA · Leitura opcional</span>
            <h2 id="ai-advisor-title">Uma segunda leitura antes da decisão.</h2>
          </div>
          <span className="ai-advisor__privacy">
            <ShieldCheck size={16} aria-hidden="true" />
            sem renda exata ou identificadores
          </span>
        </header>

        <div className="ai-advisor__facts" aria-label="Fatos calculados do cenário">
          <div>
            <span>Juros no total</span>
            <strong>{facts.interestSharePct.toFixed(1).replace(".", ",")}%</strong>
          </div>
          <div>
            <span>Seguros + tarifas</span>
            <strong>
              {(
                facts.insuranceSharePct +
                facts.adminFeesSharePct +
                facts.upfrontFeeSharePct
              )
                .toFixed(1)
                .replace(".", ",")}
              %
            </strong>
          </div>
          <div>
            <span>Queda da parcela</span>
            <strong>{facts.paymentReductionPct.toFixed(1).replace(".", ",")}%</strong>
          </div>
          <div className="ai-advisor__fact-reference">
            <span>Referência elegível</span>
            <strong>{comparisonLabel}</strong>
          </div>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => void copyDeterministicSummary()}
          >
            <Copy size={14} aria-hidden="true" />
            {copyStatus === "copied"
              ? "Resumo copiado"
              : copyStatus === "error"
                ? "Não foi possível copiar"
                : "Copiar resumo"}
          </button>
        </div>

        {visibleState.status === "idle" && (
          <div className="ai-advisor__intro">
            <div>
              <p>
                O Gemini transforma o cenário selecionado em trade-offs, pontos de atenção e
                perguntas para levar à instituição — sem alterar um centavo do cálculo.
              </p>
              <small>
                Enviamos somente valores resumidos da simulação. Nome, email, telefone, CPF,
                idade, renda bruta e cronograma completo ficam de fora. A pressão da parcela é
                reduzida a uma faixa qualitativa não reversível.
              </small>
            </div>
            <button className="button button--ai" type="button" onClick={generateInsight}>
              <Sparkles size={17} aria-hidden="true" />
              Gerar leitura com Gemini
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
        )}

        {visibleState.status === "loading" && (
          <div className="ai-advisor__loading" role="status">
            <LoaderCircle className="spin" aria-hidden="true" />
            <div>
              <strong>Lendo custo, fluxo e elegibilidade</strong>
              <span>Organizando uma análise curta, sem recalcular a simulação.</span>
            </div>
            <div className="ai-advisor__progress" aria-hidden="true">
              <i />
            </div>
          </div>
        )}

        {visibleState.status === "error" && (
          <div className="ai-advisor__error" role="alert">
            <TriangleAlert aria-hidden="true" />
            <div>
              <strong>A análise não ficou disponível</strong>
              <p>{visibleState.message}</p>
              <span>Os cálculos e o comparativo continuam funcionando normalmente.</span>
            </div>
            <button className="button button--outline" type="button" onClick={generateInsight}>
              <RefreshCcw size={16} aria-hidden="true" />
              Tentar novamente
            </button>
          </div>
        )}

        {visibleState.status === "ready" && (
          <div className="ai-advisor__result">
            <div className="ai-advisor__thesis">
              <span>Leitura do cenário selecionado</span>
              <h3>{visibleState.response.insight.headline}</h3>
              <p>{visibleState.response.insight.summary}</p>
            </div>

            <div className="ai-advisor__columns">
              <article>
                <span className="ai-advisor__label ai-advisor__label--positive">
                  <CheckCircle2 size={16} aria-hidden="true" />
                  O que sustenta a escolha
                </span>
                <ul>
                  {visibleState.response.insight.strengths.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
              <article>
                <span className="ai-advisor__label ai-advisor__label--attention">
                  <TriangleAlert size={16} aria-hidden="true" />
                  O que precisa de validação
                </span>
                <ul>
                  {visibleState.response.insight.attentionPoints.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            </div>

            <div className="ai-advisor__questions">
              <span>Perguntas para levar à instituição</span>
              <ol>
                {visibleState.response.insight.negotiationQuestions.map((question) => (
                  <li key={question}>{question}</li>
                ))}
              </ol>
            </div>

            <div className="ai-advisor__next">
              <span>Próximo passo</span>
              <p>{visibleState.response.insight.nextAction}</p>
              <button className="button button--quiet" type="button" onClick={generateInsight}>
                <RefreshCcw size={14} aria-hidden="true" />
                Atualizar leitura
              </button>
            </div>

            <footer className="ai-advisor__footnote">
              <span>
                {visibleState.response.meta.provider} · {visibleState.response.meta.cache === "hit"
                  ? "cenário recuperado"
                  : "leitura gerada agora"}
              </span>
              <small>{visibleState.response.meta.disclaimer}</small>
            </footer>
          </div>
        )}
      </div>
    </section>
  );
}
